import {
  SourceEntityMigrationType,
  type ISourceEntity,
  ISourceEntityTypes,
} from '@principle-theorem/practice-migrations/interfaces';
import {
  ISODateType,
  Timezone,
  TypeGuard,
  toFloat,
  toISODate,
} from '@principle-theorem/shared';
import { flow, isNumber, isString, isBoolean } from 'lodash';
import * as moment from 'moment-timezone';
import { BaseSourceEntity } from '../../../source/base-source-entity';
import { SourceEntity } from '../../../source/source-entity';
import {
  KEYSET_PLACEHOLDER,
  convertKeysToCamelCaseFn,
  convertNullToUndefinedFn,
  convertValueFn,
} from '../../../source/source-helpers';
import { EXACT_NULL_SENTINEL } from '../../util/helpers';
import { PatientSourceEntity } from './patient';

export const PATIENT_INVOICE_LINES_V2_RESOURCE_TYPE = 'patientInvoiceLineV2';

export const PATIENT_INVOICE_LINES_V2_SOURCE_ENTITY: ISourceEntity =
  SourceEntity.init({
    metadata: {
      label: 'Patient Invoice Lines V2 List',
      description: '',
      idPrefix: PATIENT_INVOICE_LINES_V2_RESOURCE_TYPE,
      migrationType: SourceEntityMigrationType.Automatic,
    },
  });

/**
 * The per-tooth, per-service line-item table (INVLDNT5). Each row is one
 * service on a tooth; ties back to a charge in the flat ledger (TRANS4) via
 * DebtorId + CotGuid + InvLineGroupGuid.
 *
 * Row kinds (Trnstype):
 *   65537     - Completed / billed service.
 *   536936457 - Planned / quoted service.
 *   536936466 - Prior approval item.
 */

export interface IExactV2InvoiceLineResult {
  id: string;
  line_unique?: string;
  patient_id?: string;
  /** Original Exact DebtorId (row owner) before re-attribution. Audit field. */
  source_patient_id?: string;
  course_of_treatment_id?: string;
  invoice_line_group_id?: string;
  transaction_type: number;
  /** ADA / service code e.g. '011', 'HYG-SP', 'EXAM' */
  service?: string;
  ada_code?: string;
  /** Tooth number (0 = no specific tooth, stripped by SQL NULLIF) */
  tooth_number?: number;
  surface?: number;
  booked_date?: string;
  complete_date?: string;
  planned_date?: string;
  /** Full fee before patient subsidy */
  fee_amount: number;
  /** Amount billed to patient (after subsidies) */
  patient_amount: number;
  fee_gst: number;
  patient_gst: number;
  /** Fee schedule code e.g. 'DEF', 'ACC' */
  standard_fee?: string;
  /** Dentist / provider code e.g. 'KIM' */
  dentist?: string;
  user_comment?: string;
  /** Payor code for this line e.g. 'ACC', 'SDB-C' */
  payor_code?: string;
  is_manual_fee: boolean;
  service_cost_amount: number;
  line_number: number;
  line_order: number;
  is_deleted: boolean;
}

export interface IExactV2InvoiceLine {
  id: string;
  lineUnique?: string;
  /**
   * Resolved patient this line belongs to. For agreement-funded lines this is
   * the real patient, rewritten from the holder account via the CotGuid
   * lookup (mirrors the patient transactions query). For normal lines it
   * matches `sourcePatientId`.
   */
  patientId?: string;
  /**
   * Original Exact DebtorId (row owner) before re-attribution. Audit field.
   */
  sourcePatientId?: string;
  courseOfTreatmentId?: string;
  invoiceLineGroupId?: string;
  transactionType: number;
  service?: string;
  adaCode?: string;
  toothNumber?: number;
  surface?: number;
  bookedDate?: string;
  completeDate?: string;
  plannedDate?: string;
  feeAmount: number;
  patientAmount: number;
  feeGst: number;
  patientGst: number;
  standardFee?: string;
  dentist?: string;
  userComment?: string;
  payorCode?: string;
  isManualFee: boolean;
  serviceCostAmount: number;
  lineNumber: number;
  lineOrder: number;
  isDeleted: boolean;
}

export interface IExactV2InvoiceLineTranslations {
  completeDate?: ISODateType;
}

export interface IExactV2InvoiceLineFilters {
  patientId: string;
  courseOfTreatmentId?: string;
}

function isExactV2InvoiceLine(
  data: unknown,
  logError: boolean
): data is IExactV2InvoiceLine {
  return TypeGuard.interface<IExactV2InvoiceLine>(
    {
      id: isString,
      lineUnique: TypeGuard.nilOr(isString),
      patientId: TypeGuard.nilOr(isString),
      sourcePatientId: TypeGuard.nilOr(isString),
      courseOfTreatmentId: TypeGuard.nilOr(isString),
      invoiceLineGroupId: TypeGuard.nilOr(isString),
      transactionType: isNumber,
      service: TypeGuard.nilOr(isString),
      adaCode: TypeGuard.nilOr(isString),
      toothNumber: TypeGuard.nilOr(isNumber),
      surface: TypeGuard.nilOr(isNumber),
      bookedDate: TypeGuard.nilOr(isString),
      completeDate: TypeGuard.nilOr(isString),
      plannedDate: TypeGuard.nilOr(isString),
      feeAmount: isNumber,
      patientAmount: isNumber,
      feeGst: isNumber,
      patientGst: isNumber,
      standardFee: TypeGuard.nilOr(isString),
      dentist: TypeGuard.nilOr(isString),
      userComment: TypeGuard.nilOr(isString),
      payorCode: TypeGuard.nilOr(isString),
      isManualFee: isBoolean,
      serviceCostAmount: isNumber,
      lineNumber: isNumber,
      lineOrder: isNumber,
      isDeleted: isBoolean,
    },
    logError
  )(data);
}

/**
 * Pulls billed, planned, and prior-approval lines. Prior approvals are kept
 * for ACC claim context.
 *
 * Re-attribution mirrors the patient transactions query: lines on a holder
 * ledger are rewritten to the real patient via CotGuid. Orphan lines that
 * can't be matched are dropped.
 */
const INVOICE_LINES_V2_SOURCE_QUERY = `
WITH agreement_holders AS MATERIALIZED (
  SELECT DISTINCT payorid AS debtorid
  FROM trans4
  WHERE
    NOT COALESCE(_is_deleted, false)
    AND payorid != ${EXACT_NULL_SENTINEL}
    AND payorid NOT IN (SELECT _record_id FROM localpayor)
  UNION
  SELECT _record_id AS debtorid FROM localpayor
),
cot_to_real_debtor AS MATERIALIZED (
  SELECT
    cotguid AS cot_guid,
    MIN(debtorid) AS real_debtor_id
  FROM trans4
  WHERE
    NOT COALESCE(_is_deleted, false)
    AND debtorid != ${EXACT_NULL_SENTINEL}
    AND cotguid != ${EXACT_NULL_SENTINEL}
    AND debtorid NOT IN (SELECT debtorid FROM agreement_holders)
  GROUP BY cotguid
)
SELECT
  invldnt5._record_id::TEXT                                  AS id,
  invldnt5."unique"::TEXT                                      AS line_unique,

  -- On a holder ledger? Rewrite to the real patient via CotGuid.
  COALESCE(
    CASE
      WHEN invldnt5.debtorid IN (SELECT debtorid FROM agreement_holders)
      THEN cot_to_real_debtor.real_debtor_id
      ELSE NULL
    END,
    invldnt5.debtorid
  )::TEXT                                                    AS patient_id,

  -- Audit field: the original Exact DebtorId, never rewritten.
  NULLIF(invldnt5.debtorid::TEXT, '${EXACT_NULL_SENTINEL}')        AS source_patient_id,

  NULLIF(invldnt5.cotguid::TEXT, '${EXACT_NULL_SENTINEL}')         AS course_of_treatment_id,
  NULLIF(invldnt5.invlinegroupguid::TEXT, '${EXACT_NULL_SENTINEL}') AS invoice_line_group_id,
  convert_to_integer(invldnt5.trnstype)                       AS transaction_type,
  NULLIF(invldnt5.service, '')                               AS service,
  NULLIF(invldnt5.ada, '')                                   AS ada_code,
  convert_to_integer(NULLIF(invldnt5.tn::TEXT, '0'))          AS tooth_number,
  convert_to_integer(NULLIF(invldnt5.surface::TEXT, '0'))     AS surface,
  NULLIF(invldnt5.bookeddate::TEXT, '')                      AS booked_date,
  NULLIF(invldnt5.completedate::TEXT, '')                    AS complete_date,
  NULLIF(invldnt5.planneddate::TEXT, '')                     AS planned_date,
  COALESCE(convert_to_decimal(invldnt5.fee_amount), 0)         AS fee_amount,
  COALESCE(convert_to_decimal(invldnt5.patient_amount), 0)     AS patient_amount,
  COALESCE(convert_to_decimal(invldnt5.fee_gst), 0)            AS fee_gst,
  COALESCE(convert_to_decimal(invldnt5.patient_gst), 0)        AS patient_gst,
  NULLIF(invldnt5.sfee, '')                                  AS standard_fee,
  NULLIF(invldnt5.dentist, '')                               AS dentist,
  NULLIF(invldnt5.usercomment, '')                           AS user_comment,
  NULLIF(invldnt5.payorcode, '')                             AS payor_code,
  convert_to_boolean(invldnt5.ismanualfee)                    AS is_manual_fee,
  COALESCE(convert_to_decimal(invldnt5.servicecostamount), 0)  AS service_cost_amount,
  convert_to_integer(invldnt5.linenum)                        AS line_number,
  convert_to_integer(invldnt5."order")                        AS line_order,
  COALESCE(invldnt5._is_deleted, false)                       AS is_deleted
FROM
  invldnt5
  LEFT JOIN cot_to_real_debtor
    ON cot_to_real_debtor.cot_guid = invldnt5.cotguid
WHERE
  invldnt5.trnstype IN (65537, 536936457, 536936466)
  AND COALESCE(invldnt5._is_deleted, false) = false
  -- Drop orphan holder lines that can't be re-attributed.
  AND NOT (
    invldnt5.debtorid IN (SELECT debtorid FROM agreement_holders)
    AND cot_to_real_debtor.real_debtor_id IS NULL
  )
${KEYSET_PLACEHOLDER}
`;

export type ExactV2InvoiceLine = ISourceEntityTypes<
  IExactV2InvoiceLine,
  IExactV2InvoiceLineTranslations,
  IExactV2InvoiceLineFilters
>;

export class PatientInvoiceLinesV2SourceEntity extends BaseSourceEntity<ExactV2InvoiceLine> {
  sourceEntity = PATIENT_INVOICE_LINES_V2_SOURCE_ENTITY;
  entityResourceType = PATIENT_INVOICE_LINES_V2_RESOURCE_TYPE;
  sourceQuery = INVOICE_LINES_V2_SOURCE_QUERY;
  verifySourceFn = isExactV2InvoiceLine;
  override defaultOffsetSize = 50000;
  allowOffsetJobs = true;
  override keysetPagination = { cursorColumn: 'invldnt5._record_id' };

  override requiredEntities = {
    patients: new PatientSourceEntity(),
  };

  override transformDataFn = flow([
    convertNullToUndefinedFn<IExactV2InvoiceLineResult>(),
    convertValueFn(
      toFloat,
      'fee_amount',
      'patient_amount',
      'fee_gst',
      'patient_gst',
      'service_cost_amount'
    ),
    convertKeysToCamelCaseFn<IExactV2InvoiceLineResult>(),
  ]);

  getSourceRecordId(data: IExactV2InvoiceLine): string {
    return data.id;
  }

  getSourceLabel(record: IExactV2InvoiceLine): string {
    return `${record.patientId ?? 'unknown'} ${record.service ?? record.id}`;
  }

  translate(
    data: IExactV2InvoiceLine,
    _timezone: Timezone
  ): IExactV2InvoiceLineTranslations {
    return {
      completeDate: data.completeDate
        ? toISODate(moment(data.completeDate))
        : undefined,
    };
  }

  getFilterData(
    data: IExactV2InvoiceLine,
    _timezone: Timezone
  ): IExactV2InvoiceLineFilters {
    return {
      patientId: data.patientId ?? '',
      courseOfTreatmentId: data.courseOfTreatmentId,
    };
  }
}
